import React from 'react';
import { Link } from 'react-router-dom';
import { Boxes, ArrowRight } from 'lucide-react';
import UnitPage from '../../inventory/UnitPage';
import type { DeviceTabProps } from './deviceDetail';

/**
 * THE UNIT TAB — the inventory unit page's own content, in place.
 *
 * This file deliberately contains no panels. Photo, identity, provisioning
 * checklist, condition, damage, lifecycle rail, timeline and order journey all
 * come from `UnitPage`, rendered embedded. One implementation shown in two
 * places is not duplication; a second implementation of the same panels is, and
 * the two screens would drift until they disagreed about one device.
 *
 * `/inventory/units/:unitId` remains the standalone route, unchanged.
 */
export default function UnitTab({ row }: DeviceTabProps) {
  if (row.unitId) return <UnitPage unitId={row.unitId} embedded />;

  /**
   * NO UNIT JOINED — the common case today, not an error.
   *
   * A device can check in long before anyone links it to the hardware unit it
   * physically is. That is a gap in our records, so the panel says what is
   * missing and where to close it, and says it in two lines.
   */
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-100">
        <Boxes size={22} className="text-stone-400" />
      </div>
      <h3 className="text-sm font-bold text-stone-800">Not linked to a hardware unit</h3>
      <p className="mx-auto mt-1.5 max-w-md text-[12.5px] leading-relaxed text-stone-500">
        This device checks in, but no unit record is joined to it — so its photo, install
        dates and branding versions have nowhere to come from. They all live on the unit.
      </p>
      <Link
        to="/pos-config?tab=install"
        className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl px-5 text-[13px] font-semibold text-white"
        style={{ background: '#DC143C' }}
      >
        Link it on the Install tab <ArrowRight size={15} />
      </Link>
    </div>
  );
}
